import Vue from "vue";
import VueRouter, { isNavigationFailure, NavigationFailureType } from "vue-router";
import store from "@/store";
import { getToken } from "@/utils/auth";
// 动态导入避免与 layout → request → router 的循环依赖
const Layout = () => import("@/layout/index.vue");

Vue.use(VueRouter);

function patchRouterNav(method) {
  const original = VueRouter.prototype[method];
  VueRouter.prototype[method] = function patched(location, onResolve, onReject) {
    if (onResolve || onReject) {
      return original.call(this, location, onResolve, onReject);
    }
    return original.call(this, location).catch((err) => {
      if (
        isNavigationFailure(err, NavigationFailureType.duplicated) ||
        isNavigationFailure(err, NavigationFailureType.cancelled) ||
        isNavigationFailure(err, NavigationFailureType.redirected)
      ) {
        return err;
      }
      return Promise.reject(err);
    });
  };
}

patchRouterNav("push");
patchRouterNav("replace");

export const constantRoutes = [
  {
    path: "/login",
    name: "Login",
    component: () => import("@/views/login/index.vue"),
    meta: { title: "登录" },
  },
  {
    path: "/404",
    name: "NotFound",
    component: () => import("@/views/error/404.vue"),
    meta: { title: "404" },
  },
  {
    path: "/",
    component: Layout,
    children: [
      {
        path: "dashboard",
        name: "Dashboard",
        component: () => import("@/views/dashboard/index.vue"),
        meta: { title: "工作台", permission: "dashboard:view" },
      },
      {
        path: "user/list",
        name: "UserList",
        component: () => import("@/views/user/list.vue"),
        meta: { title: "用户列表", permission: "user:view" },
        beforeEnter(to, from, next) {
          if (to.query && to.query.id) {
            next({ path: "/user/detail/" + to.query.id, replace: true });
            return;
          }
          next();
        },
      },
      {
        path: "user/detail/:id",
        name: "UserDetail",
        component: () => import("@/views/user/detail.vue"),
        meta: { title: "用户详情", permission: "user:view" },
      },
      {
        path: "user/friends",
        name: "UserFriends",
        component: () => import("@/views/user/friends.vue"),
        meta: { title: "好友关系", permission: "friend:view" },
      },
      {
        path: "user/deactivated",
        name: "UserDeactivated",
        component: () => import("@/views/user/deactivated.vue"),
        meta: { title: "注销用户", permission: "user:view" },
      },
      {
        path: "user/login-logs",
        name: "UserLoginLogs",
        component: () => import("@/views/user/login-logs.vue"),
        meta: { title: "登录日志", permission: "user:login_log" },
      },
      {
        path: "user/bans",
        name: "BanList",
        component: () => import("@/views/user/bans.vue"),
        meta: { title: "IP/设备黑名单", permission: "ban:view" },
      },
      {
        path: "user/freeze",
        name: "FreezeList",
        component: () => import("@/views/user/freeze.vue"),
        meta: { title: "冻结管理", permission: "freeze:view" },
      },
      {
        path: "user/reg-config",
        name: "RegistrationConfig",
        component: () => import("@/views/user/reg-config.vue"),
        meta: { title: "注册设置", permission: "reg:view" },
      },
      {
        path: "user/sms-logs",
        name: "SmsLogs",
        component: () => import("@/views/user/sms-logs.vue"),
        meta: { title: "短信记录", permission: "reg:view" },
      },
      {
        path: "user/client-roles",
        name: "ClientRoles",
        component: () => import("@/views/user/client-roles.vue"),
        meta: { title: "端内角色", permission: "client_role:view" },
      },
      {
        path: "group/list",
        name: "GroupList",
        component: () => import("@/views/group/list.vue"),
        meta: { title: "群组", permission: "group:view" },
      },
      {
        path: "group/default-config",
        name: "GroupDefaultConfig",
        component: () => import("@/views/group/default-config.vue"),
        meta: { title: "群默认设置", permission: "group_config:view" },
      },
      {
        path: "message/list",
        name: "MessageList",
        component: () => import("@/views/message/list.vue"),
        meta: { title: "消息记录", permission: "message:view" },
      },
      {
        path: "security/sensitive",
        name: "SensitiveWords",
        component: () => import("@/views/security/sensitive.vue"),
        meta: { title: "敏感词", permission: "sensitive:view" },
      },
      {
        path: "security/report",
        name: "ReportList",
        component: () => import("@/views/security/report.vue"),
        meta: { title: "举报处理", permission: "report:view" },
      },
      {
        path: "system/account",
        name: "AdminAccount",
        component: () => import("@/views/system/account.vue"),
        meta: { title: "管理员", permission: "admin:view" },
      },
      {
        path: "system/role",
        name: "RoleManage",
        component: () => import("@/views/system/role.vue"),
        meta: { title: "后台角色", permission: "role:view" },
      },
      {
        path: "system/menu",
        name: "MenuManage",
        component: () => import("@/views/system/menu.vue"),
        meta: { title: "菜单管理", permission: "menu:view" },
      },
      {
        path: "wallet/flows",
        name: "WalletFlows",
        component: () => import("@/views/wallet/flows.vue"),
        meta: { title: "账变流水", permission: "wallet:view" },
      },
      {
        path: "wallet/kyc",
        name: "WalletKyc",
        component: () => import("@/views/wallet/kyc.vue"),
        meta: { title: "实名审核", permission: "kyc:view" },
      },
      {
        path: "wallet/withdrawals",
        name: "WalletWithdrawals",
        component: () => import("@/views/wallet/withdrawals.vue"),
        meta: { title: "提现审核", permission: "withdraw:view" },
      },
      {
        path: "wallet/banks",
        name: "WalletBanks",
        component: () => import("@/views/wallet/banks.vue"),
        meta: { title: "银行字典", permission: "bank:view" },
      },
      {
        path: "wallet/transfers",
        name: "TransferList",
        component: () => import("@/views/wallet/transfers.vue"),
        meta: { title: "转账记录", permission: "transfer:view" },
      },
      {
        path: "wallet/checkin",
        name: "CheckinManage",
        component: () => import("@/views/wallet/checkin.vue"),
        meta: { title: "签到规则", permission: "checkin:view" },
      },
      {
        path: "wallet/recharge-orders",
        name: "RechargeOrders",
        component: () => import("@/views/wallet/recharge-orders.vue"),
        meta: { title: "充值工单", permission: "recharge_order:view" },
      },
      {
        path: "wallet/bill-report",
        name: "BillReport",
        component: () => import("@/views/wallet/bill-report.vue"),
        meta: { title: "账单报表", permission: "bill:view" },
      },
      {
        path: "operation/announcements",
        name: "Announcements",
        component: () => import("@/views/operation/announcements.vue"),
        meta: { title: "系统公告", permission: "announce:view" },
      },
      {
        path: "operation/explore",
        name: "ExploreManage",
        component: () => import("@/views/operation/explore.vue"),
        meta: { title: "探索", permission: "explore:view" },
      },
      {
        path: "operation/emojis",
        name: "EmojiManage",
        component: () => import("@/views/operation/emojis.vue"),
        meta: { title: "表情", permission: "emoji:view" },
      },
      {
        path: "redpacket/records",
        name: "RedPacketRecords",
        component: () => import("@/views/redpacket/records.vue"),
        meta: { title: "红包记录", permission: "wallet:view" },
      },
      {
        path: "system/exports",
        name: "ExportJobs",
        component: () => import("@/views/system/exports.vue"),
        meta: { title: "下载中心" },
      },
      {
        path: "wallet/approvals",
        name: "WalletApprovals",
        component: () => import("@/views/wallet/approvals.vue"),
        meta: { title: "资金复核", permission: "wallet:view" },
      },
      {
        path: "system/audit",
        name: "AuditLogs",
        component: () => import("@/views/system/audit.vue"),
        meta: { title: "操作审计", permission: "audit:view" },
      },
      {
        path: "system/site",
        name: "SiteConfig",
        component: () => import("@/views/system/site.vue"),
        meta: { title: "站点与安全", permission: "site:view" },
      },
      {
        path: "system/admin-login-logs",
        redirect: (to) => ({ path: "/system/audit", query: { ...to.query, tab: "login" } }),
      },
      {
        path: "redpacket/config",
        name: "RedPacketConfig",
        component: () => import("@/views/redpacket/config.vue"),
        meta: { title: "红包规则", permission: "redpacket:view" },
      },
      {
        path: "av/config",
        name: "AVConfig",
        component: () => import("@/views/av/config.vue"),
        meta: { title: "音视频渠道", permission: "av:view" },
      },
      {
        path: "av/calls",
        name: "AVCalls",
        component: () => import("@/views/av/calls.vue"),
        meta: { title: "通话记录", permission: "av:view" },
      },
      {
        path: "redpacket/group-settings",
        name: "GroupRedPacketSettings",
        component: () => import("@/views/redpacket/group-settings.vue"),
        meta: { title: "群红包权限", permission: "redpacket:view" },
      },
      {
        path: "wallet/recharge",
        name: "WalletRecharge",
        component: () => import("@/views/wallet/recharge.vue"),
        meta: { title: "后台充值", permission: "wallet:recharge" },
      },
    ],
  },
  { path: "*", redirect: "/404" },
];

const router = new VueRouter({
  mode: "history",
  scrollBehavior: () => ({ x: 0, y: 0 }),
  routes: constantRoutes,
});

let profileLoaded = false;

router.beforeEach(async (to, from, next) => {
  const token = getToken();
  // 根路径不再用路由 redirect，避免与守卫跳登录互相取消导航
  if (to.path === "/") {
    next(token ? "/dashboard" : "/login");
    return;
  }
  if (to.path === "/login") {
    if (token) {
      next({ path: "/dashboard" });
    } else {
      next();
    }
    return;
  }
  if (!token) {
    profileLoaded = false;
    next({ path: "/login", query: { redirect: to.fullPath } });
    return;
  }
  if (!profileLoaded) {
    try {
      await store.dispatch("fetchProfile");
      profileLoaded = true;
    } catch (e) {
      store.dispatch("logout");
      profileLoaded = false;
      next({ path: "/login", query: { redirect: to.fullPath } });
      return;
    }
  }
  if (to.meta && to.meta.permission && !store.getters.hasPermission(to.meta.permission)) {
    if (to.path === "/dashboard") {
      next();
      return;
    }
    next({ path: "/dashboard" });
    return;
  }
  next();
});

router.afterEach((to) => {
  const title = (to.meta && to.meta.title) || "管理后台";
  document.title = title + " - IM 管理后台";
});

export default router;
